import sortBy from "lodash/sortBy";
import flatMap from "lodash/flatMap";

export const groupBy = function (xs, key) {
  return xs.reduce(function (rv, x) {
    (rv[x[key]] = rv[x[key]] || []).push(x);
    return rv;
  }, {});
};

export const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export const retryOperation = (fn, data, delay, times) => {
  function rejectDelay(reason) {
    return new Promise(function(resolve, reject) {
      setTimeout(reject.bind(null, reason), delay);
    });
  }

  let p = Promise.reject();

  for(let i = 0; i < times; i++) {
    p = p.catch(() => fn(data)).catch(rejectDelay);
  }

  return p.then(result => result).catch(error => error);
};

export const getOrderGeo = (order) => order.deliveryGeo;

const DEFAULT_GEO = { lat: 0, lng: 0 };

export const getLatLng = (order) => {
  const geo = getOrderGeo(order);

  if (!geo) {
    return DEFAULT_GEO;
  }

  const [lat, lng] = geo.split(',');

  return { lat: parseFloat(lat), lng: parseFloat(lng) };
};

export const generatePoints = (orders = []) =>
  orders
    .filter((order) => !order.selected)
    .map((o) => {
      const { lat, lng } = getLatLng(o);
      return {
        type: 'Feature',
        properties: {
          cluster: false,
          orderId: o.id,
          order: o,
        },
        geometry: { type: 'Point', coordinates: [lng, lat] },
      };
    });

const colors = {
  0: '#5B74BF',
  1: '#ED5D54',
  2: '#49CC75',
  3: '#F7A997',
  4: '#5EE1ED',
  5: '#FFC26C',
};

export const getRouteColor = (index) => {
  if (colors[index]) {
    return colors[index];
  }

  return colors[5];
};

export const getNotCompletedIncomePriceColor = () => 'rgb(255, 238, 239)';

export const routeOrdersToStages = (routeOrders) => groupBy(
  sortBy(
    routeOrders,
    ['stage', 'ordering']
  ),
  'stage'
);

export const stagesToRouteOrders = (stages) => {
  const reorderedStages = {};

  Object.keys(stages).map(key => {
    reorderedStages[key] = stages[key].map((ro, index) => ({
      ...ro,
      stage: key,
      ordering: index
    }))
  });

  return flatMap(reorderedStages);
};

export const getNewStageIndex = (stages) => {
  const last = Object.keys(stages).pop();
  return last ? parseInt(last) + 1 : 0;
};

export const timeDiffIntervalForHumans = (durationInMin) => {
  const hours = Math.floor(durationInMin / 60);
  const minutes = durationInMin % 60;

  return __('routes.formated_driver_break_duration', { hours, minutes });
};

export const RouteElementType = Object.freeze({
  ROUTE_ORDER: 0,
  DRIVER_BREAK_TIME: 1,
  BUFFER_TIME: 2,
});

/**
 * Route elements that carry a real order. Driver breaks and buffers are rows of
 * their own in `route_orders` with `order_id = NULL`, so anything reading
 * `element.order` must filter them out first.
 */
export const getDeliverableRouteOrders = (routeOrders) =>
  (routeOrders ?? []).filter(
    (element) => element?.type === RouteElementType.ROUTE_ORDER && element.order
  );

export const OrderType = Object.freeze({
  ORDER: 'order',
  RETURN: 'return',
});

// Mirrors App\Enums\OrderStatus (PHP)
export const OrderStatus = Object.freeze({
  NEW: 1,
  TRANSIT_FROM_MANUFACTURER: 2,
  IN_STOCK: 3,
  SENT: 5,
  RECEIVED: 6,
  CANCELLED: 7,
  TRANSPORT_FAILURE: 8,
  CHANGE_DELIVERY_DATE: 9,
});

// Orders in these statuses should not appear on the routing map
// (already shipped, delivered, or cancelled — not actionable for routing)
export const HIDDEN_FROM_MAP_STATUSES = Object.freeze([
  OrderStatus.SENT,
  OrderStatus.RECEIVED,
  OrderStatus.CANCELLED,
]);

export const isHiddenFromMap = (order) =>
  HIDDEN_FROM_MAP_STATUSES.includes(order?.statusId);

// Tryb wydania zwrotów zależy od trasy, nie od pojedynczych zleceń.
export const isReturnIssueRoute = (route) => Boolean(route?.isReturnIssue);
